import { action, extendObservable } from 'mobx';
import PropTypes from 'prop-types';
import { PropTypes as MobxPropTypes } from 'mobx-react';

export const SPLIT_ROW = '\n';

export default class BulkEditorStore {
    static PropType = PropTypes.shape({
        search: PropTypes.string,
        urlPatterns: MobxPropTypes.arrayOrObservableArray,
        width: PropTypes.number,
        height: PropTypes.number,
        fontSize: PropTypes.number,
        minWidth: PropTypes.number,
        numbersColor: PropTypes.string,
        goTo: PropTypes.func,
        lineHeight: PropTypes.number,
        fontFamily: PropTypes.string,
        lastGoToUpdate: PropTypes.number,
        lastSearchFocus: PropTypes.number,
        top: PropTypes.number,
        validator: PropTypes.func,
        validators: MobxPropTypes.arrayOrObservableArray,
        lineHighlight: PropTypes.string,
        lastDupedPattern: PropTypes.string,
        lastDupedPatternIndex: PropTypes.number,
        duplicatePatternsIndexes: PropTypes.object,
        duplicatePatternsCount: PropTypes.number,
        warningBackground: PropTypes.string,
        numbersWarningColor: PropTypes.string,
        hasWarnings: PropTypes.func,
        needsWarning: PropTypes.func,
    });

    constructor(data = {}) {
        this.lineHighlight = 'rgba(183, 28, 28, .14)';
        this.warningBackground = 'rgba(255, 152, 0, 0.22)';
        this.numbersWarningColor = 'rgba(255, 152, 0, 1)';
        this.fontSize = 15;
        this.lineHeight = 1.4;
        this.height = 200;
        this.numbersColor = 'rgba(66, 66, 66, .87)';
        this.minWidth = 50;
        this.minHeight = 100;
        this.validator = () => true;
        this.caseSensitiveSearch = false;
        this.validators = [];

        extendObservable(this, /** @class BulkEditorStore */{
            search: '',
            searchRow: null,
            searchIndexInRow: null,
            top: 0,
            width: 800,
            marginLeft: 0,
            wasFocusedInLastDraw: false,
            changed: false,
            invalidUrlPatternsCount: 0,
            urlPatterns: [],
            firstInvalidUrl: 0,
            urlPatternsSize: 0,
            searchCount: 0,
            indexOfFirstInvalidUrl: undefined,
            failedValidatorsIndexes: [],
            lastGoToUpdate: 0,
            lastSearchFocus: 0,
            duplicatePatternsCount: 0,
            duplicatePatternsIndexes: {},
            getSearchIndex: () => {
                if (!this.search || this.search === '' || this.searchRow === null || this.searchIndexInRow === null) {
                    return 0;
                }
                let counter = 0;
                const searchText = this.getStringForSearch(this.search);
                const rowToScan = Math.min(this.searchRow, this.urlPatterns.length);
                for (let i = 0; i < rowToScan; i += 1) {
                    counter += this.getStringForSearch(this.urlPatterns[i]).split(searchText).length - 1;
                }
                if (this.searchRow < this.urlPatterns.length) {
                    counter += this.getStringForSearch(this.urlPatterns[this.searchRow]
                        .substring(0, this.searchIndexInRow + this.search.length)).split(searchText).length - 1;
                }
                return counter;
            },
            isValid: () => {
                return this.invalidUrlPatternsCount === 0;
            },
            hasChanged: () => {
                return this.changed;
            },
            getCanonicalValue: (value) => value,
        }, data);

        this.setUrlPatterns(this.urlPatterns);
    }

    setPrevUrlPatterns = action((urlPatterns) => {
        this.prevUrlPatterns = urlPatterns ? [...urlPatterns] : undefined;
    });

    areUrlPatternsChanged(urlPatterns) {
        if (!this.prevUrlPatterns) {
            return false;
        }
        if (this.prevUrlPatterns.length === 0 && (urlPatterns.filter((cur) => cur !== '').length === 0)) {
            return false;
        }
        if (this.prevUrlPatterns.length !== urlPatterns.length) {
            return true;
        }
        return !this.prevUrlPatterns.every((cur, index) => cur === urlPatterns[index]);
    }

    setUrlPatterns = action((urlPatterns) => {
        if (urlPatterns === undefined || urlPatterns === null) {
            urlPatterns = [];
        }
        this.urlPatterns.replace(urlPatterns);
        this.changed = this.areUrlPatternsChanged(urlPatterns);
        this.updateUrlBulkInfo(urlPatterns);
    });

    updateUrlBulkInfo = action((urlPatterns = this.urlPatterns) => {
        const bulkData = this.getBulkData(urlPatterns);
        this.invalidUrlPatternsCount = bulkData.invalidUrlPatternsCount || 0;
        this.firstInvalidUrl = bulkData.firstInvalidUrl;
        this.urlPatternsSize = bulkData.urlCount || 0;
        this.indexOfFirstInvalidUrl = bulkData.indexOfFirstInvalidUrl;
        this.failedValidatorsIndexes = bulkData.failedValidatorsIndexes;
        this.searchCount = bulkData.searchCount;
        this.lastDupedPattern = bulkData.lastDupedPattern;
        this.lastDupedPatternIndex = bulkData.lastDupedPatternIndex;
        this.duplicatePatternsIndexes = bulkData.duplicatePatternsIndexes;
        this.duplicatePatternsCount = bulkData.duplicatePatternsCount;
    });

    getBulkData(urlPatterns) {
        this.failedValidatorsIndexes = [];
        const data = urlPatterns.reduce((acc, url, index) => {
            if (url !== '') {
                const canonicalValue = this.getCanonicalValue(url);
                const uniqueIndexes = acc.duplicatePatternsIndexes[canonicalValue];

                if (!uniqueIndexes) {
                    acc.duplicatePatternsIndexes[canonicalValue] = [index];
                } else {
                    // side effects, should be more efficient than concat or spread
                    uniqueIndexes.push(index);

                    acc.duplicatePatternsCount += 1;
                    acc.lastDupedPattern = url;
                    acc.lastDupedPatternIndex = index;
                }
            }
            if (this.search && this.search !== '') {
                acc.searchCount = acc.searchCount ? acc.searchCount +
                    this.getStringForSearch(url).split(this.getStringForSearch(this.search))
                        .length - 1 : this.getStringForSearch(url)
                    .split(this.getStringForSearch(this.search)).length - 1;
            }
            if (url !== '') {
                acc.urlCount = acc.urlCount ? acc.urlCount + 1 : 1;
                let isInvalid = false;
                if (this.validators.length > 0) {
                    this.validators.forEach((validator, validatorIndex) => {
                        if (!this.failedValidatorsIndexes.includes(validatorIndex) && !validator(url)) {
                            this.failedValidatorsIndexes.push(validatorIndex);
                        }
                    });
                    acc.failedValidatorsIndexes = this.failedValidatorsIndexes;
                    isInvalid = this.failedValidatorsIndexes.length > 0;
                } else {
                    isInvalid = !this.validator(url);
                }
                if (isInvalid) {
                    acc.invalidUrlPatternsCount = acc.invalidUrlPatternsCount ? acc.invalidUrlPatternsCount + 1 : 1;
                    if (!acc.firstInvalidUrl) {
                        acc.firstInvalidUrl = url;
                        acc.indexOfFirstInvalidUrl = index;
                    }
                }
            }
            return acc;
        }, {
            duplicatePatternsIndexes: {},
            duplicatePatternsCount: 0
        });
        // Don't store entries that are not duplicates. The initial approach was do have a map of all, but
        // that leads to duplicating the data, which takes a lot of memory the longer the list is
        Object.keys(data.duplicatePatternsIndexes).forEach((p) => {
            if (data.duplicatePatternsIndexes[p].length < 2) {
                // using 'delete' is not as efficient and is effectively the same
                data.duplicatePatternsIndexes[p] = undefined;
            }
        });
        // ----
        return data;
    }

    getSearchData(val) {
        return this.urlPatterns.reduce((acc, url) => {
            if (val && val !== '') {
                val = this.getStringForSearch(val);
                acc.searchCount = acc.searchCount ? acc.searchCount + this.getStringForSearch(url)
                    .split(val).length - 1 : this.getStringForSearch(url).split(val).length - 1;
            }
            return acc;
        }, {});
    }

    searchForward = action(() => {
        if (this.wasFocusedInLastDraw) {
            this.setWasFocusedInLastDraw(false);
        } else if (this.isExistSearch()) {
            let searchData = this.findNextSearch(this.searchRow, this.search.length + this.searchIndexInRow);
            if (!searchData) {
                searchData = this.findNextSearch(0, 0);
            }
            if (searchData) {
                this.setSearchData(searchData.searchRow, searchData.searchIndexInRow);
            }
        }
        if (this.getSearchIndex() === 1 && this.searchCount === 1) {
            this.lastSearchFocus = new Date().getTime();
        }
    });

    searchBack = action(() => {
        if (this.wasFocusedInLastDraw) {
            this.setWasFocusedInLastDraw(false);
        } else if (this.isExistSearch()) {
            let searchData;
            if (this.getSearchIndex() > 0) {
                searchData = this.findPrevSearch(this.searchRow, this.searchIndexInRow);
            }
            if (!searchData) {
                searchData = this.findPrevSearch(this.urlPatterns.length - 1,
                    this.urlPatterns[this.urlPatterns.length - 1].length);
            }
            if (searchData) {
                this.setSearchData(searchData.searchRow, searchData.searchIndexInRow);
            }
            if (this.getSearchIndex() === 1 && this.searchCount === 1) {
                this.lastSearchFocus = new Date().getTime();
            }
        }
    });

    isExistSearch() {
        return this.search && this.search.length > 0;
    }

    getStringForSearch(str) {
        return this.caseSensitiveSearch ? str : str.toLowerCase();
    }

    findNextSearch(row, index) {
        if (this.urlPatterns.length === 0) {
            return null;
        }
        const searchText = this.getStringForSearch(this.search);
        const indexInSubRow = this.getStringForSearch(this.urlPatterns[row]).substring(index).indexOf(searchText);
        if (indexInSubRow > -1) {
            return { searchRow: row, searchIndexInRow: indexInSubRow + index };
        }
        for (let i = row + 1; i < this.urlPatterns.length; i += 1) {
            const searchIndexInRow = this.getStringForSearch(this.urlPatterns[i]).indexOf(searchText);
            if (searchIndexInRow > -1) {
                return { searchRow: i, searchIndexInRow };
            }
        }
        return null;
    }

    findPrevSearch(row, index) {
        if (this.urlPatterns.length === 0) {
            return null;
        }
        const searchText = this.getStringForSearch(this.search);
        const indexInSubRow = this.getStringForSearch(this.urlPatterns[row])
            .substring(0, index).lastIndexOf(searchText);
        if (indexInSubRow > -1) {
            return { searchRow: row, searchIndexInRow: indexInSubRow };
        }
        for (let i = row - 1; i >= 0; i -= 1) {
            const searchIndexInRow = this.getStringForSearch(this.urlPatterns[i]).lastIndexOf(searchText);
            if (searchIndexInRow > -1) {
                return { searchRow: i, searchIndexInRow };
            }
        }
        return null;
    }

    needsWarning = (pattern) => !!this.duplicatePatternsIndexes[this.getCanonicalValue(pattern)];

    hasWarnings = () => this.duplicatePatternsCount > 0;

    updateNextSearchByPos = action((startFrom, urlPatterns) => {
        if (this.search && this.search.length > 0) {
            const str = urlPatterns.join(SPLIT_ROW);
            const searchText = this.getStringForSearch(this.search);
            let rest = this.getStringForSearch(str.substring(startFrom));
            if (rest.indexOf(searchText) === -1) {
                const indexOfLast = str.lastIndexOf(searchText);
                if (indexOfLast === -1) {
                    return;
                }
                rest = str.substring(indexOfLast);
            }
            const r = rest.substring(rest.indexOf(searchText));
            const restSplitToRows = r.split(SPLIT_ROW);
            const row = urlPatterns.length - restSplitToRows.length;
            const indexInRow = urlPatterns[row].length - restSplitToRows[0].length;
            this.searchRow = row;
            this.searchIndexInRow = indexInRow;
        }
    });

    updateSearchMarks = action((firstLine) => {
        const searchData = this.findNextSearch(firstLine, 0) || this.findPrevSearch(firstLine, 0);
        if (searchData) {
            this.setSearchData(searchData.searchRow, searchData.searchIndexInRow);
        } else {
            this.setSearchData();
        }
    });

    setSearch = action((newValue) => {
        if (newValue === undefined || newValue === null) {
            newValue = '';
        }
        const searchData = this.getSearchData(newValue);
        this.searchCount = searchData.searchCount || 0;
        this.search = newValue;
    });

    goTo = action((index) => {
        this.top = index;
        this.lastGoToUpdate = new Date().getTime();
    });

    setSearchData = action((row = null, rowIndex = null) => {
        this.searchRow = row;
        this.searchIndexInRow = rowIndex;
        this.lastSearchFocus = new Date().getTime();
    });

    setWasFocusedInLastDraw = action((wasFocusedInLastDraw) => {
        this.wasFocusedInLastDraw = wasFocusedInLastDraw;
    });

    setMarginLeft = action((marginLeft) => {
        this.marginLeft = marginLeft;
    });
}
